/**
 * The function registry (`specs/M2-formula.md` §5) — a registry, not a switch (CLAUDE.md #6).
 *
 * **Exactly the eight functions the account uses** `[for§1]`, with the semantics the census
 * pins down:
 *
 * | Function | Uses | Semantics measured |
 * |---|---|---|
 * | `TODAY` | 9 | today's date, no argument |
 * | `IF` | 8 | `IF(cond, then, else)`, nested three deep, branches of different types |
 * | `YEAR` | 8 | int |
 * | `MONTH` | 4 | 1–12 |
 * | `ROUND` | 4 | `ROUND(number, digits)`, digits 0 or 1 |
 * | `DAYS` | 4 | **signed** day difference, argument order matters |
 * | `DAY` | 2 | 1–31 |
 * | `SUM` | 1 | used as a one-argument wrapper, not an aggregate |
 *
 * Anything else is an `unknown_function` error rather than a guess: monday has ~50 functions
 * and inventing semantics for the 42 nobody here uses would break CLAUDE.md rule 3 (q338).
 */
import { formulaError, isFormulaDate, isFormulaError, } from './ast.js';
import { toDate, toNumber } from './coerce.js';
function dateParts(value) {
    const [year, month, day] = value.iso.split('-').map(Number);
    return { year, month, day };
}
function requireDate(value, fn) {
    const date = toDate(value);
    if (date === null)
        return formulaError('argument', `${fn} expects a date`);
    return date;
}
export const FORMULA_FUNCTIONS = [
    {
        name: 'IF',
        minArgs: 3,
        maxArgs: 3,
        signature: 'IF(condition, value_if_true, value_if_false)',
        description: 'Returns one of two values depending on a condition.',
        call(args) {
            const [condition, whenTrue, whenFalse] = args;
            if (condition !== undefined && isFormulaError(condition))
                return condition;
            const truthy = condition === true ||
                (typeof condition === 'number' && condition !== 0) ||
                (typeof condition === 'string' && condition !== '');
            return (truthy ? whenTrue : whenFalse) ?? null;
        },
    },
    {
        name: 'TODAY',
        minArgs: 0,
        maxArgs: 0,
        signature: 'TODAY()',
        description: "Today's date in the account timezone.",
        call(_args, ctx) {
            // The account timezone, not the server's: the same formula must not flip at midnight UTC.
            const iso = new Intl.DateTimeFormat('en-CA', {
                timeZone: ctx.timezone ?? 'UTC',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }).format(ctx.now);
            return { kind: 'date', iso };
        },
    },
    {
        name: 'YEAR',
        minArgs: 1,
        maxArgs: 1,
        signature: 'YEAR(date)',
        description: 'The year of a date.',
        call(args) {
            const date = requireDate(args[0] ?? null, 'YEAR');
            if (!isFormulaDate(date))
                return date;
            return dateParts(date).year;
        },
    },
    {
        name: 'MONTH',
        minArgs: 1,
        maxArgs: 1,
        signature: 'MONTH(date)',
        description: 'The month of a date, 1–12.',
        call(args) {
            const date = requireDate(args[0] ?? null, 'MONTH');
            if (!isFormulaDate(date))
                return date;
            return dateParts(date).month;
        },
    },
    {
        name: 'DAY',
        minArgs: 1,
        maxArgs: 1,
        signature: 'DAY(date)',
        description: 'The day of the month, 1–31.',
        call(args) {
            const date = requireDate(args[0] ?? null, 'DAY');
            if (!isFormulaDate(date))
                return date;
            return dateParts(date).day;
        },
    },
    {
        name: 'DAYS',
        minArgs: 2,
        maxArgs: 2,
        signature: 'DAYS(end_date, start_date)',
        description: 'Whole days between two dates; negative when the first is earlier.',
        call(args) {
            const end = requireDate(args[0] ?? null, 'DAYS');
            if (!isFormulaDate(end))
                return end;
            const start = requireDate(args[1] ?? null, 'DAYS');
            if (!isFormulaDate(start))
                return start;
            /*
             * Signed, first minus second — the census proves the order matters: `DAYS({timeline#End},
             * {date1})` is "days left" (positive while the deadline is ahead) and
             * `days({date1},{timeline#End})>0` is "overdue" `[for§4 #2,#3]`. Both readings are the
             * same subtraction.
             */
            const endMs = Date.parse(`${end.iso}T00:00:00Z`);
            const startMs = Date.parse(`${start.iso}T00:00:00Z`);
            return Math.round((endMs - startMs) / 86_400_000);
        },
    },
    {
        name: 'ROUND',
        minArgs: 1,
        maxArgs: 2,
        signature: 'ROUND(number, digits)',
        description: 'Rounds a number to the given number of decimals.',
        call(args) {
            const value = toNumber(args[0] ?? null);
            if (value === null)
                return formulaError('argument', 'ROUND expects a number');
            const digitsRaw = args.length > 1 ? toNumber(args[1] ?? null) : 0;
            const digits = digitsRaw === null ? 0 : Math.trunc(digitsRaw);
            const factor = 10 ** digits;
            return Math.round(value * factor) / factor;
        },
    },
    {
        name: 'SUM',
        minArgs: 1,
        maxArgs: 32,
        signature: 'SUM(number, …)',
        description: 'Adds its arguments. Used here as a one-argument wrapper.',
        call(args) {
            let total = 0;
            for (const arg of args) {
                if (isFormulaError(arg))
                    return arg;
                const value = toNumber(arg);
                if (value !== null)
                    total += value;
            }
            return total;
        },
    },
];
const BY_NAME = new Map(FORMULA_FUNCTIONS.map((fn) => [fn.name, fn]));
export function findFormulaFunction(name) {
    return BY_NAME.get(name.toUpperCase());
}
/** The names the editor's function list offers, in the census's usage order. */
export const FORMULA_FUNCTION_NAMES = FORMULA_FUNCTIONS.map((fn) => fn.name);
//# sourceMappingURL=functions.js.map